import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { adminAuth, adminApi, formatApiErrorDetail } from "../lib/api";
import { NaaviLogo, ShapeSquare, ShapeCircle } from "../components/brand/Shapes";
import { ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", { email, password });
      adminAuth.set(data.token);
      toast.success("Welcome back to the Naavi admin console.");
      navigate(next);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] relative overflow-hidden flex items-center justify-center px-5 py-10" data-testid="admin-login-page">
      <ShapeSquare size={220} className="absolute -top-16 -left-16 opacity-90 rotate-12" color="#648DE5" />
      <ShapeCircle size={140} className="absolute bottom-10 right-10 opacity-90" color="#C6D947" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#0F1B47] mb-6 font-semibold"
          data-testid="back-to-site"
        >
          <ArrowLeft size={14} />
          Back to website
        </Link>

        <div className="bg-[#FDFDF9] rounded-[32px] p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(15,27,71,0.25)] border border-[#0F1B47]/8">
          <div className="flex items-center gap-3 mb-6">
            <NaaviLogo height={36} />
          </div>
          <div className="inline-flex items-center gap-2 bg-[#F5F5F0] rounded-full px-3 py-1.5 mb-5">
            <ShieldCheck size={13} className="text-[#3AB0D6]" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#0F1B47]">
              Admin console
            </span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0F1B47] leading-tight mb-2">
            Sign in.
          </h1>
          <p className="text-[#4A5568] text-sm font-light mb-8">
            For Naavi staff. Use your admissions email and password.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@naaviearlyyears.edu.in"
                required
                className={inputCls}
                data-testid="admin-login-email"
              />
            </Field>
            <Field label="Password">
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className={inputCls}
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#0F1B47]/60"
                  aria-label="Toggle password visibility"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>

            {error && (
              <div
                className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-center pt-1">
              <Link
                to="/admin/forgot-password"
                className="text-xs font-semibold text-[#0F1B47] underline underline-offset-4 hover:text-[#1a2b6b]"
                data-testid="admin-forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-[#0F1B47]/8">
            <p className="text-xs text-[#718096] font-light">
              Are you a parent?{" "}
              <Link to="/parent/login" className="font-semibold text-[#0F1B47] underline underline-offset-2">
                Use the parent portal →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
